// Cable Size Calculator: field metadata + client-side per-field validation, used for
// instant inline feedback as the user types. The actual sizing math (voltage-drop and
// ampacity checks against BS 7671:2018 / IEC 60364-5-52 tables) and the authoritative
// (server-side) validation live in the main backend — see src/lib/toolsApi.js
// (cableSizeToolApi) and backend/tools/cable_size_tool.py.

export const STANDARD_SIZES_MM2 = [1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95, 120, 150, 185, 240];

export const AWG_LABEL_BY_MM2 = {
  1.5: '16 AWG', 2.5: '14 AWG', 4: '12 AWG', 6: '10 AWG', 10: '8 AWG',
  16: '6 AWG', 25: '3 AWG', 35: '2 AWG', 50: '1/0 AWG', 70: '2/0 AWG',
  95: '3/0 AWG', 120: '250 kcmil', 150: '300 kcmil', 185: '350 kcmil', 240: '500 kcmil',
};

export const SYSTEM_TYPES = [
  { value: 'dc', label: 'DC' },
  { value: 'single_phase', label: 'Single-Phase AC' },
  { value: 'three_phase', label: 'Three-Phase AC' },
];

export const CONDUCTOR_MATERIALS = [
  { value: 'copper', label: 'Copper' },
  { value: 'aluminum', label: 'Aluminum' },
];

export const INSULATION_RATINGS = [
  { value: 'pvc70', label: 'PVC, 70°C' },
  { value: 'xlpe90', label: 'XLPE / thermosetting, 90°C' },
];

export const INSTALL_METHODS = [
  { value: 'enclosed_wall', label: 'Enclosed, in wall (Method A)' },
  { value: 'enclosed_conduit', label: 'Enclosed, conduit/trunking (Method B)' },
  { value: 'clipped_direct', label: 'Clipped direct (Method C)' },
  { value: 'free_air_touching', label: 'Free air, bunched (Method F)' },
  { value: 'free_air_spaced', label: 'Free air, spaced (Method F/G)' },
];

export const REFERENCE_STANDARDS = [
  { value: 'iec60364', label: 'IEC 60364-5-52' },
  { value: 'nec', label: 'NEC (label only)' },
  { value: 'iso6722', label: 'ISO 6722 (label only)' },
];

export const GROUPING_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 12, 16, 20];

export const DEFAULT_INPUTS = {
  systemType: 'dc',
  inputMode: 'current',
  loadCurrentA: 50,
  loadPowerKw: 10,
  supplyVoltageV: 12,
  cableLengthM: 5,
  conductorMaterial: 'copper',
  maxVoltageDropPercent: 3,

  ambientTempC: 30,
  insulationRating: 'pvc70',
  installMethod: 'clipped_direct',
  groupingCircuits: 1,
  powerFactor: 0.8,
  parallelConductors: 1,
  dutyCycle: 'continuous',
  dutyFactorPercent: 100,
  referenceStandard: 'iec60364',
};

// min / max mirror NUMERIC_FIELD_SPECS in backend/core/lookup/cable_size_specs.py.
export const NUMERIC_FIELD_SPECS = [
  { key: 'loadCurrentA', label: 'Load Current', unit: 'A', min: 0, max: 3000, strictlyPositive: true },
  { key: 'loadPowerKw', label: 'Load Power', unit: 'kW', min: 0, max: 3000, strictlyPositive: true },
  { key: 'supplyVoltageV', label: 'Supply Voltage', unit: 'V', min: 0, max: 1000, strictlyPositive: true },
  { key: 'cableLengthM', label: 'Cable Length (one-way)', unit: 'm', min: 0, max: 2000, strictlyPositive: true },
  { key: 'maxVoltageDropPercent', label: 'Max Allowable Voltage Drop', unit: '%', min: 0.1, max: 10, strictlyPositive: true },

  { key: 'ambientTempC', label: 'Ambient Temperature', unit: '°C', min: 10, max: 80, strictlyPositive: false },
  { key: 'groupingCircuits', label: 'Circuits Grouped Together', unit: '', min: 1, max: 20, strictlyPositive: true },
  { key: 'powerFactor', label: 'Power Factor', unit: '', min: 0.1, max: 1, strictlyPositive: true },
  { key: 'parallelConductors', label: 'Parallel Conductors per Phase', unit: '', min: 1, max: 8, strictlyPositive: true },
  { key: 'dutyFactorPercent', label: 'Intermittent Duty Factor', unit: '%', min: 10, max: 100, strictlyPositive: true },
];

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}
